import { ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators, AbstractControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DropdownOption } from "@cms/shared-library";
import { Address } from 'src/app/models/address.model';
import { TPApplicationInfoModel } from 'src/app/models/training-provider-application.model';
import { LookupObjectService } from 'src/app/services/lookup-object/lookup-object.service';
import { commaDelimitedEmailValidatorPattern, emailValidatorPattern, phoneValidatorPattern } from 'src/app/utils/validators.util';
import { TrainingProgramApplicationUtils } from 'src/app/utils/training-program-application.util';
import { PredictiveAddressSearchComponent } from '../predictive-address-search/predictive-address-search.component';

@Component({
    selector: 'app-applicant-contact-information',
    templateUrl: './applicant-contact-information.component.html',
    styleUrls: ['./applicant-contact-information.component.scss'],
    standalone: false
})
export class ApplicantContactInformationComponent implements OnInit {
  @Input() showErrorsOverride: boolean = false
  @Input() isDSA: boolean = true
  @ViewChild(PredictiveAddressSearchComponent) predictiveAddressSearchComponent: PredictiveAddressSearchComponent;
  currentLang: string = 'en';
  @Input() tpApplicationInformation: TPApplicationInfoModel;
  @Input() editApprovedMode: boolean = false;
  @Input() soseMode: boolean = false;
  tpApplicationInfoForm: FormGroup;

  roleInOrganizationOptions: DropdownOption[] = [];
  allRoles: DropdownOption[] = [];
  role: string = '';
  @Output() mailingAddressChanged = new EventEmitter<Address>;

  constructor(
    @Inject('LookupObjectService') public lookupObjectService: LookupObjectService,
    public translateService: TranslateService,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef) {
    this.currentLang = translateService.currentLang;
    translateService.onLangChange.subscribe(lang => {
      this.currentLang = lang.lang;
    });
  }

  ngOnInit() {
    this.initData();
    this.buildForm();
   
    this.lookupObjectService.getLookupObjectsByGroupName("AuthorityRole")
      .subscribe((data) => {
        this.allRoles = data.map(x => new DropdownOption(x.Value, x.ValueFr, x.LookupObjectId));
        this.handleRoleFilter(this.isDSA);
        if (this.editApprovedMode) {
          this.tpApplicationInfoForm.controls['role'].disable();
          this.tpApplicationInfoForm.controls['role'].clearValidators();
        }
      });
  }

  valid(): boolean {
    let isFormValid = true;
    Object.keys(this.tpApplicationInfoForm.controls).forEach((control: string) => {
      const typedControl: AbstractControl = this.tpApplicationInfoForm.controls[control];
      if (typedControl.invalid && control != 'AddressForm' && control != 'role') {
        isFormValid = false;
        console.error(control);
      }
    });

    return isFormValid && (this.soseMode ? true : this.predictiveAddressSearchComponent.valid());
  }

  initData(): void {
    if (!this.tpApplicationInformation) {
      const tpAim = new TPApplicationInfoModel();

      tpAim.FirstName = '';
      tpAim.LastName = '';
      tpAim.Phone = '';
      tpAim.Email = '';
      tpAim.AlternateEmailAddress = '';
      tpAim.Role = '';
      tpAim.MailingAddress = new Address('00000000-0000-0000-0000-000000000000', '', '', '', '', '', '', 'Canada');

      this.tpApplicationInformation = tpAim;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.roleInOrganizationOptions && this.tpApplicationInfoForm && changes['isDSA']) {
      this.handleRoleFilter(changes['isDSA'].currentValue)
    }
  }

  setLoggedInUserAsContact(soseMode = false, readOnlyMode = false): void {
    this.tpApplicationInfoForm.controls['emailAddress'].setValue(this.tpApplicationInformation.Email);
    this.tpApplicationInfoForm.controls['firstName'].setValue(this.tpApplicationInformation.FirstName);
    this.tpApplicationInfoForm.controls['lastName'].setValue(this.tpApplicationInformation.LastName);

    if (soseMode) {
      if (readOnlyMode) {
        this.tpApplicationInfoForm.controls['emailAddress'].disable();
        this.tpApplicationInfoForm.controls['firstName'].disable();
        this.tpApplicationInfoForm.controls['lastName'].disable();
      } else {
        this.tpApplicationInfoForm.controls['emailAddress'].enable();
        this.tpApplicationInfoForm.controls['firstName'].enable();
        this.tpApplicationInfoForm.controls['lastName'].enable();
      }
    }
  }

  addressChanged($event: Address) {
    this.mailingAddressChanged.emit($event);
  }

  handleRoleFilter(isDsa: boolean) {
    if (isDsa) {
      this.roleInOrganizationOptions = this.allRoles.filter(x => x.value.toLowerCase() == TrainingProgramApplicationUtils.DSA.toLowerCase())
      if (this.roleInOrganizationOptions && this.roleInOrganizationOptions.length > 0) {
        this.tpApplicationInfoForm.controls['role'].setValue(this.roleInOrganizationOptions[0].value)
        this.tpApplicationInfoForm.controls['role'].disable()
        this.role = this.roleInOrganizationOptions[0].value
        this.cdr.detectChanges()
      }
    } else {
      this.roleInOrganizationOptions = this.allRoles.filter(x => x.value.toLowerCase() != TrainingProgramApplicationUtils.DSA.toLowerCase() && 
                                        x.value.toLowerCase() != TrainingProgramApplicationUtils.ASA.toLowerCase() && 
                                        x.value.toLowerCase() != TrainingProgramApplicationUtils.AffiliateAdministratorId.toLowerCase() && 
                                        x.value.toLowerCase() != TrainingProgramApplicationUtils.AffiliateInstructorId.toLowerCase())

      if (this.roleInOrganizationOptions.map(x => {return x.value}).indexOf(this.tpApplicationInfoForm.controls['role'].value) == -1) {
        this.tpApplicationInfoForm.controls['role'].reset()
        this.tpApplicationInfoForm.controls['role'].enable()
        this.role = ''
        this.cdr.detectChanges()
      }
    }
  }

  buildForm() {    
    this.tpApplicationInfoForm = this.formBuilder.group({
      firstName: new FormControl(this.tpApplicationInformation!.FirstName, [Validators.maxLength(256), Validators.required]),
      lastName: new FormControl(this.tpApplicationInformation!.LastName, [Validators.maxLength(256), Validators.required]),
      phone: new FormControl(this.tpApplicationInformation!.Phone, [Validators.maxLength(11), Validators.required, Validators.pattern(phoneValidatorPattern),]),
      emailAddress: new FormControl(this.tpApplicationInformation!.Email, [Validators.pattern(emailValidatorPattern), Validators.required]),
      alternateEmailAddress: new FormControl(this.tpApplicationInformation!.AlternateEmailAddress, [Validators.pattern(commaDelimitedEmailValidatorPattern)]),
      role: new FormControl(this.tpApplicationInformation!.Role, [Validators.required]),
      country: new FormControl('Canada', [Validators.required]),
      AddressForm: this.formBuilder.group({
        address: new FormControl(this.tpApplicationInformation!.MailingAddress!.AddressLine, [Validators.maxLength(500), Validators.required]),
        aptSuitUnit: new FormControl(this.tpApplicationInformation!.MailingAddress!.AptSuiteUnit, [Validators.maxLength(500)]),
        city: new FormControl(this.tpApplicationInformation!.MailingAddress!.CityTown, [Validators.required, Validators.maxLength(100)]),
        province: new FormControl(this.tpApplicationInformation!.MailingAddress!.Province, [Validators.required]),
        postalCode: new FormControl(this.tpApplicationInformation!.MailingAddress!.PostalCode, [Validators.required, Validators.maxLength(7)]),
      }),    
      poBox: new FormControl(this.tpApplicationInformation!.MailingAddress!.PoBox, []),
    });
    if (this.soseMode) {
      this.tpApplicationInfoForm.addControl('middleInitial', new FormControl(this.tpApplicationInformation.MiddleName));
      this.tpApplicationInfoForm.addControl('position', new FormControl(this.tpApplicationInformation.Position, [Validators.required]));
    }

    this.tpApplicationInfoForm.get('poBox')!.valueChanges.subscribe(() => {
      this.addressChanged(this.getAddress());
    });
  }

  private getAddress(): Address {
    if (this.predictiveAddressSearchComponent?.AddressForm) {
      return new Address('00000000-0000-0000-0000-000000000000'
    , this.predictiveAddressSearchComponent.AddressForm.get('postalCode')!.value
    , this.predictiveAddressSearchComponent.AddressForm.get('address')!.value       
    , this.predictiveAddressSearchComponent.AddressForm.get('aptSuitUnit')!.value
    , this.tpApplicationInfoForm.controls['poBox'].value
    , this.predictiveAddressSearchComponent.AddressForm.get('city')!.value
    , this.predictiveAddressSearchComponent.AddressForm.get('province')!.value    
    , this.tpApplicationInfoForm.controls['country'].value);
    } else {
      return new Address('00000000-0000-0000-0000-000000000000'
    , this.tpApplicationInfoForm.get('AddressForm.postalCode')!.value
    , this.tpApplicationInfoForm.get('AddressForm.address')!.value       
    , this.tpApplicationInfoForm.get('AddressForm.aptSuitUnit')!.value
    , this.tpApplicationInfoForm.controls['poBox'].value
    , this.tpApplicationInfoForm.get('AddressForm.city')!.value
    , this.tpApplicationInfoForm.get('AddressForm.province')!.value    
    , this.tpApplicationInfoForm.controls['country'].value);
    }   
  }

  resetApplicantContactInformation() {
    this.predictiveAddressSearchComponent.clearFormFields(this.tpApplicationInfoForm.get('AddressForm.address')?.value);
    this.tpApplicationInfoForm.get('AddressForm.address')?.reset();
    this.buildForm();
  }

  saveApplicantContactInformation(): TPApplicationInfoModel {
    this.tpApplicationInformation = {
      FirstName: this.tpApplicationInfoForm.controls['firstName'].value,
      MiddleName: this.soseMode ? this.tpApplicationInfoForm.controls['middleInitial']?.value : '',
      LastName: this.tpApplicationInfoForm.controls['lastName'].value,
      Phone: this.tpApplicationInfoForm.controls['phone'].value,
      PhoneExtension: this.soseMode ? this.tpApplicationInfoForm.controls['extension']?.value : '',
      Position: this.soseMode ? this.tpApplicationInfoForm.controls['position']?.value : '',
      Email: this.tpApplicationInfoForm.controls['emailAddress'].value,
      AlternateEmailAddress: this.tpApplicationInfoForm.controls['alternateEmailAddress'].value,
      Role: this.role,
      MailingAddress: this.getAddress()
    };
    return this.tpApplicationInformation
  }

  setContactInfo(tpApplicationInfoModel: TPApplicationInfoModel): void {
    this.tpApplicationInformation = tpApplicationInfoModel;
    this.tpApplicationInformation.Phone = tpApplicationInfoModel.Phone.split('x')[0]
    this.buildForm();
    if(!this.soseMode) this.predictiveAddressSearchComponent.setAddress(tpApplicationInfoModel!.MailingAddress!);
  }

}
